import React, { useState, useEffect } from 'react';
import { Table, Button, Spinner, Badge, Alert } from 'react-bootstrap';
import { format } from 'date-fns';
import accountLockoutService from '../../services/accountLockoutService';

/**
 * 잠긴 계정 목록을 표시하는 컴포넌트
 */
const LockedAccountsTable = ({ accounts, onUnlock, loading }) => {
  const [unlockingId, setUnlockingId] = useState(null);
  const [error, setError] = useState(null);
  const [maxAttempts, setMaxAttempts] = useState(5); // 기본값 5로 설정
  
  // 컴포넌트 마운트 시 최대 로그인 시도 횟수 조회
  useEffect(() => {
    const fetchMaxAttempts = async () => {
      try {
        const settings = await accountLockoutService.getLoginAttemptSettings();
        if (settings && settings.maxAttempts) {
          setMaxAttempts(settings.maxAttempts);
        }
      } catch (err) {
        console.error('최대 로그인 시도 횟수 조회 실패:', err);
      }
    };
    
    fetchMaxAttempts();
  }, []);

  // 계정 잠금 해제 처리
  const handleUnlock = async (userIdx) => {
    setUnlockingId(userIdx);
    setError(null);
    try {
      await accountLockoutService.unlockAccount(userIdx);
      onUnlock(userIdx);
    } catch (err) {
      setError(`계정 잠금 해제 실패: ${err.message}`);
    } finally {
      setUnlockingId(null);
    }
  };

  // 날짜/시간 포맷팅 함수
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'yyyy-MM-dd HH:mm:ss');
    } catch (e) {
      return dateString;
    }
  };

  // 남은 시간 계산 함수
  const calculateRemainingTime = (lockedUntil) => {
    if (!lockedUntil) return '-';
    
    const now = new Date();
    const lockExpiry = new Date(lockedUntil);
    
    if (now > lockExpiry) {
      return '잠금 만료됨';
    }
    
    const diffMs = lockExpiry - now;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMins / 60);
    
    if (diffHrs > 0) {
      return `${diffHrs}시간 ${diffMins % 60}분`;
    }
    return `${diffMins}분`;
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">로딩 중...</span>
        </Spinner>
      </div>
    );
  }

  if (!accounts || accounts.length === 0) {
    return <Alert variant="info">잠긴 계정이 없습니다.</Alert>;
  }

  return (
    <>
      {error && <Alert variant="danger">{error}</Alert>}
      <Table striped bordered hover responsive className="mt-3">
        <thead className="bg-light">
          <tr>
            <th>ID</th>
            <th>사용자명</th>
            <th>역할</th>
            <th>로그인 시도</th>
            <th>잠금 이유</th>
            <th>잠금 시간</th>
            <th>남은 시간</th>
            <th>관리</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((account) => (
            <tr key={account.idx}>
              <td>{account.id}</td>
              <td>{account.name}</td>
              <td>
                <Badge bg={account.role === 'super_admin' ? 'danger' : 'primary'}>
                  {account.role}
                </Badge>
              </td>
              <td>
                {account.login_attempts === maxAttempts ? (
                  <span className="text-danger fw-bold">{account.login_attempts} / {maxAttempts}</span>
                ) : (
                  <span>{account.login_attempts} / {maxAttempts}</span>
                )}
              </td>
              <td>{account.locked_reason || '최대 로그인 시도 초과'}</td>
              <td>{formatDateTime(account.locked_at)}</td>
              <td>{calculateRemainingTime(account.locked_until)}</td>
              <td>
                <Button 
                  variant="outline-success" 
                  size="sm" 
                  onClick={() => handleUnlock(account.idx)}
                  disabled={unlockingId === account.idx}
                >
                  {unlockingId === account.idx ? (
                    <>
                      <Spinner 
                        as="span" 
                        animation="border" 
                        size="sm" 
                        role="status" 
                        aria-hidden="true" 
                      />
                      <span className="visually-hidden">처리 중...</span>
                    </>
                  ) : '잠금 해제'}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
};

export default LockedAccountsTable;
